<template>
  <div class="grid grid-cols-3 gap-6 py-2">
    <div
      v-for="(screenshot, index) in editableScreenshots"
      :key="index"
      class="relative"
      :draggable="screenshot.deleted ? 'false' : 'true'"
      @dragstart="onDragStart($event, index)"
      @dragover.prevent
      @drop="onDrop(index)"
    >
      <AssetPreview
        :class="{ 'opacity-20': screenshot.deleted }"
        :content-type="screenshot.contentType || ''"
        :url="screenshot.url || ''"
      />
      <div class="flex items-center justify-between">
        <button
          class="btn-link-neutral-s my-4 flex items-center"
          :disabled="screenshot.deleted && nonDeletedScreenshots.length >= props.maxScreenshots"
          :data-testid="`delete-screenshot-${index}`"
          @click="toggleDeleteScreenshot(screenshot)"
        >
          <MdiSvg :path="screenshot.deleted ? mdiArrowULeftTop : mdiTrashCanOutline" :size="18" class="mr-2" />
          {{ screenshot.deleted ? t('Reviews.product.edit.screenshots.restore') : t('Reviews.product.edit.screenshots.delete') }}
        </button>
        <div v-if="screenshot.deleted || !screenshot.id" class="pr-0.5 text-label-m-bold text-subtle">
          {{ screenshot.deleted ? t('Reviews.product.edit.screenshots.deletedFile') : t('Reviews.product.edit.screenshots.newFile') }}
        </div>
      </div>
    </div>

    <MediaUpload
      v-if="nonDeletedScreenshots.length < props.maxScreenshots"
      ref="fileInput"
      class="w-full"
      :accept="''"
      aspect-ratio="16:9"
      :error="getValidationErrors(v$.screenshot)"
      :hide-preview="true"
      :short-hint="uploaderHints"
      data-testid="screenshot-upload"
      @upload="handleScreenshotUpload"
    />
    <div
      v-else-if="props.upsellOnLimit"
      data-testid="screenshots-limit-upsell"
      class="flex aspect-video flex-col items-center justify-center gap-3 rounded-sm border border-primary bg-surface-elevation-2 p-4 text-center"
    >
      <p class="text-text-main mx-4 text-headline-xs">
        <LockIcon class="-mt-1 mr-1 inline-block lucide-icon-s text-icon-primary" />
        {{ t('Reviews.product.edit.screenshots.limitReachedUpsell.label', { maxNumberOfFiles: props.maxScreenshots }) }}
      </p>
      <a :href="props.upsellCtaLink" target="_blank" class="btn-primary-color-xs" data-testid="screenshots-limit-upsell-cta">
        {{ props.upsellCtaLabel }}
        <LockIcon class="text-icon-primary-inverse lucide-icon-s" />
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiTrashCanOutline, mdiArrowULeftTop } from '@mdi/js'
import { MdiSvg } from '@ramp106/omrjs-core-ui'
import { LockIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate } from '@vuelidate/core'
import { onMounted, computed, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ReviewsEntityScreenshotEditData } from '@/components/ReviewsManagement/types.ts'
import type { ProductById } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper.ts'
import { fileMaxSizeRule, fileMimeTypeRule, getValidationErrors } from '@/helpers/validationHelper.ts'
import AssetPreview from '@/ui/AssetPreview.vue'
import MediaUpload from '@/ui/MediaUpload.vue'

import { MAX_NUMBER_OF_SCREENSHOTS, MAX_SCREENSHOT_MB_SIZE } from './constants.ts'

const props = withDefaults(
  defineProps<{
    subject: 'agency' | 'product'
    entityId: string
    screenshots: NonNullable<ProductById['screenshots']>
    maxScreenshots?: number
    upsellOnLimit?: boolean
    upsellCtaLabel?: string
    upsellCtaLink?: string
  }>(),
  {
    maxScreenshots: MAX_NUMBER_OF_SCREENSHOTS,
    upsellOnLimit: false,
    upsellCtaLabel: '',
    upsellCtaLink: '',
  },
)

const emit = defineEmits<{
  (e: 'change', value: ReviewsEntityScreenshotEditData[]): void
}>()

const { t } = useI18n()
const newScreenshot = ref<File | null>(null)
const fileInput = ref()

const screenshotMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/webm']
const screenshotAcceptedMimeTypes = screenshotMimeTypes.map((type) => `${type.split('/')[1]}`).join(', ')

const rules = computed(() => ({
  screenshot: {
    fileSize: fileMaxSizeRule(t, { megaBytes: MAX_SCREENSHOT_MB_SIZE }),
    fileType: fileMimeTypeRule(t, { mimeTypes: screenshotMimeTypes }),
  },
}))

const uploaderHints = computed(() => {
  return [screenshotAcceptedMimeTypes, `Max. ${MAX_SCREENSHOT_MB_SIZE} MB`]
})

const v$ = useVuelidate(rules, { screenshot: newScreenshot })
const editableScreenshots = ref<ReviewsEntityScreenshotEditData[]>([])
const nonDeletedScreenshots = computed(() => editableScreenshots.value.filter((s) => !s.deleted))

const initScreenshots = () => {
  editableScreenshots.value = props.screenshots.map((screenshot) => ({
    id: screenshot.id ?? null,
    contentType: screenshot.contentType,
    url: screenshot.url,
    updated: false,
    deleted: false,
  }))
}

const handleScreenshotUpload = async (file: File) => {
  if (!file) {
    return
  }
  track('profile_item_add', {
    surface: 'editor',
    entity_type: props.subject === 'product' ? 'tool' : 'service',
    entity_id: props.entityId,
    item_type: 'screenshot',
  })
  newScreenshot.value = file

  await nextTick()
  if (v$.value.screenshot.$invalid) {
    showNotification({
      headline: t('Reviews.product.edit.screenshots.invalidScreenshot.headline'),
      message: t('Reviews.product.edit.screenshots.invalidScreenshot.message', {
        sizeMb: MAX_SCREENSHOT_MB_SIZE,
        fileFormat: screenshotAcceptedMimeTypes,
      }),
      type: 'error',
      timeout: 10000,
    })
    newScreenshot.value = null
    return
  }

  const data = {
    id: null,
    contentType: file.type,
    url: URL.createObjectURL(file),
    file,
    updated: false,
    deleted: false,
  }
  editableScreenshots.value.splice(editableScreenshots.value.length + 1, 0, data)
  fileInput.value?.reset()
  updatePositions()
}

const toggleDeleteScreenshot = (screenshot: ReviewsEntityScreenshotEditData) => {
  if (!screenshot.id) {
    const index = editableScreenshots.value.findIndex((s) => s === screenshot)
    URL.revokeObjectURL(screenshot.url)
    editableScreenshots.value.splice(index, 1)
    updatePositions()
    return
  }

  screenshot.deleted = !screenshot.deleted
}

const updatePositions = () => {
  nonDeletedScreenshots.value.forEach((screenshot, index) => {
    screenshot.position = index + 1
    screenshot.updated = true
  })
}

const draggableScreenshot = ref<ReviewsEntityScreenshotEditData | null>(null)

const onDragStart = (event: DragEvent, index: number) => {
  if (event.dataTransfer && event.target) {
    event.dataTransfer.effectAllowed = 'move'
  }
  draggableScreenshot.value = editableScreenshots.value[index]
}

const onDrop = (index: number) => {
  if (!draggableScreenshot.value) {
    return
  } else if (!draggableScreenshot.value.deleted) {
    moveScreenshot(draggableScreenshot.value, index)
  }
  draggableScreenshot.value = null
}

const moveScreenshot = (screenshot: ReviewsEntityScreenshotEditData, index: number) => {
  const fromIndex = editableScreenshots.value.findIndex((s) => s === screenshot)
  const element = editableScreenshots.value.splice(fromIndex, 1)[0]
  editableScreenshots.value.splice(index, 0, element)
  updatePositions()
}

onMounted(() => {
  initScreenshots()
})

watch(
  () => editableScreenshots.value,
  () => {
    emit('change', editableScreenshots.value)
  },
  { deep: true },
)
</script>
